import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    OnInit,
    ViewEncapsulation,
    inject,
    output,
    signal,
} from '@angular/core';
import { MAT_SNACK_BAR_DATA, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { Subscription, interval } from 'rxjs';

export interface UndoSnackBarData {
    message: string;
    durationMs: number;
}

@Component({
    selector: 'hq-undo-snack-bar',
    templateUrl: './undo-snack-bar.component.html',
    styleUrl: './undo-snack-bar.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        MatSnackBarModule,
        MatButtonModule,
        MatProgressBarModule,
    ],
})
export class UndoSnackBarComponent implements OnInit, OnDestroy {
    readonly data = inject<UndoSnackBarData>(MAT_SNACK_BAR_DATA);

    /** Emitted when the user clicks Undo — the service cancels the commit. */
    readonly undo = output<void>();
    /** Emitted when the user clicks Dismiss — the service commits immediately. */
    readonly dismiss = output<void>();
    /** Emitted exactly once when the countdown reaches zero — the service commits. */
    readonly expired = output<void>();

    /** 100 → 0 over durationMs; drives the determinate countdown progress bar. */
    readonly progress = signal(100);

    private intervalSub: Subscription | undefined;
    /** Timestamp when the current interval segment started (undefined when paused). */
    private startedAt: number | undefined;
    /** Milliseconds elapsed across all completed segments before the current one. */
    private elapsedBeforePause = 0;
    /** Guards against emitting `expired` more than once. */
    private hasExpired = false;

    ngOnInit(): void {
        this.startInterval();
    }

    ngOnDestroy(): void {
        this.intervalSub?.unsubscribe();
    }

    /** Pause the countdown (e.g. on mouseenter or focusin). No-op if already paused or expired. */
    pause(): void {
        if (!this.intervalSub) { return; }
        this.elapsedBeforePause += Date.now() - (this.startedAt ?? Date.now());
        this.intervalSub.unsubscribe();
        this.intervalSub = undefined;
        this.startedAt = undefined;
    }

    /** Resume the countdown (e.g. on mouseleave or focusout). No-op if expired or already ticking. */
    resume(): void {
        if (this.hasExpired || this.intervalSub) { return; }
        this.startInterval();
    }

    onUndo(): void {
        this.undo.emit();
    }

    onDismiss(): void {
        this.dismiss.emit();
    }

    private startInterval(): void {
        this.startedAt = Date.now();
        this.intervalSub = interval(50).subscribe(() => {
            const elapsed = this.elapsedBeforePause + (Date.now() - this.startedAt!);
            const remaining = this.data.durationMs - elapsed;
            this.progress.set(Math.max(0, (remaining / this.data.durationMs) * 100));
            if (remaining <= 0 && !this.hasExpired) {
                this.hasExpired = true;
                this.intervalSub?.unsubscribe();
                this.intervalSub = undefined;
                this.expired.emit();
            }
        });
    }
}
